// src/components/SimpleComponents/SimpleInputArray.jsx
// *******************************************************************************
// SIMPLE INPUT ARRAY - Tag-based input for arrays of strings
// *******************************************************************************
// Uses AntD Select mode="tags" with dropdown hidden to create a clean
// tag input field. User types a value, presses Enter (or comma), and it
// becomes a tag. Multiple values are stored as a string array in the form.
//
// Usage:
//   <SimpleInputArray name="purchaseOrderNumber" label="order" />
//   // Form value: ['49147', '49200', '50123']
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { Select } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import { useSimpleFormContext } from './SimpleFormProvider';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleInputArray = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  placeholder,
  tokenSeparators = [','],
  maxTagCount,

  validateTrigger = 'onBlur',
  showFeedback = false,

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Controlled search value to prevent text duplication on Enter→Tab sequence
  const [searchValue, setSearchValue] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      normalize={normalizeArray}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        mode="tags"
        style={{ width: '100%', minHeight: 40, ...style }}
        tokenSeparators={tokenSeparators}
        open={false}
        searchValue={searchValue}
        onSearch={setSearchValue}
        onSelect={() => setSearchValue('')}
        onBlur={() => setSearchValue('')}
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        maxTagCount={maxTagCount}
        styles={{ suffix: { display: 'none' } }}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleInputArray;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Normalize form value to always be a clean string array
 * Filters out empty strings and non-string values
 */
function normalizeArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(v => typeof v === 'string' && v.trim().length > 0);
}
